import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Building2, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use useRef to prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          setError("No session ID found in URL");
          setTimeout(() => navigate("/login"), 2000);
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for user data via backend
        const response = await axios.post(
          `${API}/auth/google/callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.success) {
          // Store JWT token for backward compatibility with existing auth system
          if (response.data.access_token) {
            localStorage.setItem("token", response.data.access_token);
          }
          
          // Clear the URL fragment and redirect to dashboard
          window.history.replaceState(null, "", window.location.pathname);
          
          // Navigate to dashboard with user data to skip auth check
          navigate("/", { 
            replace: true,
            state: { user: response.data.user }
          });
        } else {
          setError(response.data.message || "Authentication failed");
          setTimeout(() => navigate("/login"), 2000);
        }
      } catch (err) {
        console.error("Auth callback error:", err);
        setError(err.response?.data?.detail || "Authentication failed. Please try again.");
        setTimeout(() => navigate("/login"), 3000);
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-6 p-8">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#4B5E40] to-[#8FA385] flex items-center justify-center">
          <Building2 className="w-8 h-8 text-white" />
        </div>
        
        {error ? (
          <div className="text-center">
            <p className="text-red-600 font-medium mb-2">{error}</p>
            <p className="text-slate-500 text-sm">Redirecting to login...</p>
          </div>
        ) : (
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#4B5E40] mx-auto mb-4" />
            <p className="text-slate-700 font-medium">Completing sign in...</p>
            <p className="text-slate-500 text-sm mt-1">Please wait while we set up your account</p>
          </div>
        )}
      </div>
    </div>
  );
}
